import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articleData, defaultArticle } from '@/mocks/articleData';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'chapters' | 'info'>('chapters');

  const article = (slug && articleData[slug as keyof typeof articleData]) || defaultArticle;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    url: `http://yashisz.com/article/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫漫画网站',
      url: 'http://yashisz.com/',
    },
  };

  const faqJsonLd = article.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  } : null;

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      {faqJsonLd && <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>}

      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-5">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '漫画大全', href: '/category' },
                  { label: article.category, href: `/category?cat=${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 w-48 md:w-56">
                <div className="rounded-2xl overflow-hidden bg-background-200">
                  <div className="aspect-[3/4]">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`${article.title} - 韩漫漫画网站`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                    {article.category}
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    {article.status}
                  </span>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {article.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-4">
                  作者：<strong className="text-foreground-950">{article.author}</strong>
                </p>

                <div className="flex items-center gap-6 mb-4">
                  <div className="flex items-center gap-1.5">
                    <span className="text-accent-500 text-lg font-heading font-bold">{article.rating}</span>
                    <div className="flex">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < Math.round(article.rating) ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-xs text-foreground-400">({article.ratingCount}人评价)</span>
                  </div>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>{article.views} 次阅读
                  </span>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-file-list-3-line mr-1"></i>共 {article.totalChapters} 话
                  </span>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {article.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-200 text-foreground-600 text-xs cursor-pointer hover:bg-background-300 transition-colors"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="inline-flex items-center justify-center px-8 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line mr-2"></i>开始阅读
                  </button>
                  <button className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-background-200 text-foreground-700 text-sm font-medium hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line mr-2"></i>加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="flex items-center gap-1 mb-6 bg-background-200 p-1 rounded-full w-fit">
                  <button
                    onClick={() => setActiveTab('chapters')}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === 'chapters' ? 'bg-background-50 text-foreground-950' : 'text-foreground-500'
                    }`}
                  >
                    章节列表
                  </button>
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === 'info' ? 'bg-background-50 text-foreground-950' : 'text-foreground-500'
                    }`}
                  >
                    作品介绍
                  </button>
                </div>

                {activeTab === 'chapters' && (
                  <div className="space-y-2">
                    {article.chapters.map((ch) => (
                      <div
                        key={ch.num}
                        className="flex items-center justify-between px-5 py-3.5 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-foreground-400 font-medium w-12">
                            第{ch.num}话
                          </span>
                          <span className="text-sm text-foreground-950">{ch.title}</span>
                        </div>
                        <span className="text-xs text-foreground-400">{ch.date}</span>
                      </div>
                    ))}
                    {article.chapters.length === 0 && (
                      <p className="text-center text-foreground-500 py-10">暂无章节信息</p>
                    )}
                  </div>
                )}

                {activeTab === 'info' && (
                  <div className="prose prose-sm max-w-none">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {article.longDescription}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="font-heading font-semibold text-foreground-950 mb-4">相关推荐</h3>
                <div className="space-y-4">
                  {article.relatedComics.map((rel) => (
                    <Link
                      key={rel.id}
                      to="/"
                      className="flex items-center gap-4 p-3 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <div className="w-14 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-sm font-medium text-foreground-950 truncate">{rel.title}</h4>
                        <div className="flex items-center gap-1 text-xs text-foreground-500 mt-1">
                          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                          {rel.rating}
                        </div>
                      </div>
                    </Link>
                  ))}
                  {article.relatedComics.length === 0 && (
                    <p className="text-sm text-foreground-500">暂无相关推荐</p>
                  )}
                </div>

                {article.faqs.length > 0 && (
                  <div className="mt-8">
                    <h3 className="font-heading font-semibold text-foreground-950 mb-4">常见问题</h3>
                    <div className="space-y-3">
                      {article.faqs.map((faq, i) => (
                        <div key={i} className="p-4 rounded-xl bg-background-100">
                          <p className="text-sm font-medium text-foreground-950 mb-2">{faq.question}</p>
                          <p className="text-xs text-foreground-600">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}